import React from "react";
import { Search, X, ArrowDownAZ } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "pending_approval", label: "Pending" },
  { id: "scheduled", label: "Scheduled" },
  { id: "paused", label: "Paused" },
  { id: "published", label: "Posted" },
];
const PLATFORMS = ["all", "Instagram", "Facebook", "LinkedIn", "X / Twitter"];
const SOURCES = [
  { id: "all", label: "Any source" },
  { id: "manual", label: "Manual" },
  { id: "autopilot", label: "Autopilot" },
];
const SORTS = [
  { id: "newest", label: "Newest first" },
  { id: "scheduled_asc", label: "Scheduled ↑" },
  { id: "scheduled_desc", label: "Scheduled ↓" },
];

export default function ContentFilters({ filters, onChange, counts }) {
  const update = (patch) => onChange?.({ ...filters, ...patch });
  return (
    <div className="space-y-3" data-testid="content-filters">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[color:var(--text-3)]" />
          <Input
            value={filters.q || ""}
            onChange={(e) => update({ q: e.target.value })}
            placeholder="Search captions…"
            data-testid="content-search"
            className="pl-8 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
          />
          {filters.q ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-3)] hover:text-[color:var(--text-1)]"
              onClick={() => update({ q: "" })}
              data-testid="content-search-clear"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <Select value={filters.platform || "all"} onValueChange={(v) => update({ platform: v })}>
          <SelectTrigger className="max-w-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="filter-platform">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PLATFORMS.map((p) => (
              <SelectItem key={p} value={p}>{p === "all" ? "Any platform" : p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.source || "all"} onValueChange={(v) => update({ source: v })}>
          <SelectTrigger className="max-w-[160px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="filter-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCES.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.sort || "newest"} onValueChange={(v) => update({ sort: v })}>
          <SelectTrigger className="max-w-[170px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="filter-sort">
            <ArrowDownAZ className="h-3.5 w-3.5 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="status-chips">
        {STATUSES.map((s) => {
          const count = counts?.[s.id] ?? counts?.["all"];
          const active = (filters.status || "all") === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => update({ status: s.id })}
              data-testid={`status-chip-${s.id}`}
              className={
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-all " +
                (active
                  ? "chip-grad"
                  : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
              }
            >
              {s.label}
              {typeof count === "number" ? (
                <span className="text-[10px] font-mono-tabular text-[color:var(--text-3)]">{count}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
